import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router";
import { useWorkspaceContext } from "@core/context";
import { usePromiseToast, useTranslate } from "@core/hooks";
import { saveApprovalAnnotations } from "@modules/docs/annotations/services";
import type { BackendAnnotationMutationPayload } from "@modules/docs/annotations/types/TBackendAnnotation";
import type { ServiceError } from "@core/types/TServiceError";

export type SaveAnnotationsPayload = BackendAnnotationMutationPayload;

export function useCreateApprovalAnnotations() {
  const { workspaceId } = useWorkspaceContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const approvalId = searchParams.get("approval_id") ?? "";
  const { successKey, errorKey } = usePromiseToast();
  const { t } = useTranslate("documents");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveAnnotationsPayload) =>
      saveApprovalAnnotations(workspaceId, projectId!, approvalId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-annotations", workspaceId, projectId, approvalId],
      });
      successKey(t, "annotations.save-success");
    },
    onError: (err: ServiceError) => {
      errorKey(t, "errors." + err.code, err.fields);
    },
  });
}
